import { Check, CircleHelp } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';

import styles from './ConfusedButton.module.css';

type SentimentStatus = 'error' | 'idle' | 'pending' | 'success';

interface ConfusedButtonProps {
  disabled?: boolean;
  disabledReason?: string;
  onRecord: () => void;
  status: SentimentStatus;
}

function getLabel(status: SentimentStatus) {
  if (status === 'pending') {
    return 'Recording…';
  }

  if (status === 'success') {
    return 'Recorded!';
  }

  return "I'M CONFUSED";
}

/**
 * The name starts with the words the button shows (WCAG 2.5.3), so a voice
 * user who says "click I'm confused" reaches it. While recording and once
 * recorded, the words it shows are its name.
 */
function getAccessibleName(status: SentimentStatus) {
  return status === 'idle' || status === 'error'
    ? "I'm confused: record that this moment is confusing"
    : undefined;
}

export function ConfusedButton({
  disabled = false,
  disabledReason,
  onRecord,
  status,
}: ConfusedButtonProps) {
  const isDisabled = disabled || status === 'pending';
  const tooltipCopy =
    disabledReason ??
    "Pressing this records that you were confused at this point in the video. Only cumulative, anonymous data is shared with your instructor.";
  // aria-disabled, not disabled: a disabled button takes no pointer or focus,
  // so the tooltip saying why it cannot be pressed could never show.
  const button = (
    <Button
      aria-disabled={isDisabled ? 'true' : undefined}
      aria-label={getAccessibleName(status)}
      className={styles.button}
      data-status={status}
      onClick={isDisabled ? undefined : onRecord}
      size="small"
      title={tooltipCopy}
      variant="primary"
    >
      {status === 'success' ? (
        <Check aria-hidden="true" className={styles.icon} size={16} />
      ) : (
        <CircleHelp aria-hidden="true" className={styles.icon} size={16} />
      )}
      <span aria-hidden="true" className={styles.shrug}>
        ¯\_(ツ)_/¯
      </span>
      {getLabel(status)}
    </Button>
  );

  return <Tooltip content={tooltipCopy}>{button}</Tooltip>;
}
